import React from 'react';
import TileGroup from '../TileGroup';
import Day from './Day';
import { getDayOfWeek } from '../shared/dates';
import { setDate, startOfDay, startOfMonth, endOfMonth, getMonth } from 'date-fns';

type DaysProps = {
	activeStartDate: Date;
	showFixedNumberOfWeeks?: boolean;
	showNeighboringMonth?: boolean;
} & Omit<
	React.ComponentProps<typeof TileGroup>,
	'dateTransform' | 'dateType' | 'end' | 'renderTile' | 'start'
> &
	Omit<React.ComponentProps<typeof Day>, 'classes' | 'currentMonthIndex' | 'date' | 'point'>;

export default function Days(props: DaysProps) {
	const {
		activeStartDate,
		hover,
		showFixedNumberOfWeeks,
		showNeighboringMonth,
		value,
		...otherProps
	} = props;

	const hasFixedNumberOfWeeks = showFixedNumberOfWeeks || showNeighboringMonth;
	const dayOfWeek = getDayOfWeek(startOfDay(startOfMonth(activeStartDate)));
	const offset = hasFixedNumberOfWeeks ? 0 : dayOfWeek;

	/**
	 * Defines on which day of the month the grid shall start. If we simply show current
	 * month, we obviously start on day one, but if showNeighboringMonth is set to
	 * true, we need to find the beginning of the week the first day of the month is in.
	 */
	const start = (hasFixedNumberOfWeeks ? -dayOfWeek : 0) + 1;

	/**
	 * Defines on which day of the month the grid shall end. If we simply show current
	 * month, we need to stop on the last day of the month, but if showNeighboringMonth
	 * is set to true, we need to find the end of the week the last day of the month is in.
	 */
	const end = (() => {
		if (showFixedNumberOfWeeks) {
			// Always show 6 weeks
			return start + 6 * 7 - 1;
		}

		const monthLastDate = startOfDay(endOfMonth(activeStartDate));
		const daysInMonth = monthLastDate.getDate();

		if (showNeighboringMonth) {
			const daysUntilEndOfTheWeek = 7 - getDayOfWeek(monthLastDate) - 1;
			return daysInMonth + daysUntilEndOfTheWeek;
		}

		return daysInMonth;
	})();

	return (
		<TileGroup
			className="FSHCalendarView__month-view__days"
			count={7}
			dateTransform={day => {
				return startOfDay(setDate(activeStartDate, day));
			}}
			hover={hover}
			end={end}
			renderTile={({ date, ...otherTileProps }) => (
				<Day
					key={Math.floor(date.getTime() / 1000)}
					{...otherProps}
					{...otherTileProps}
					activeStartDate={activeStartDate}
					currentMonthIndex={getMonth(activeStartDate)}
					date={date}
				/>
			)}
			offset={offset}
			start={start}
			value={value}
		/>
	);
}
